import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import i18next from 'i18next';
import type { UserPublic } from '@connexio/shared';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from '@connexio/shared/constants';
import type { SupportedLocale } from '@connexio/shared/constants';
import { cacheMotionPreference } from '@/lib/motionBootstrap';

const LOCALE_STORAGE_KEY = 'freelancecrm-locale';

function syncLocale(user: UserPublic) {
  const locale: SupportedLocale = SUPPORTED_LOCALES.includes(user.locale as SupportedLocale)
    ? (user.locale as SupportedLocale)
    : DEFAULT_LOCALE;
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  if (i18next.language !== locale) {
    void i18next.changeLanguage(locale);
  }
}

function syncMotion(user: UserPublic) {
  const pref = user.motionPreference;
  if (pref === 'system' || pref === 'full' || pref === 'reduced') {
    cacheMotionPreference(pref);
  }
}

interface AuthState {
  user: UserPublic | null;
  accessToken: string | null;
  isAuthenticated: boolean;

  setUser: (user: UserPublic) => void;
  setAccessToken: (token: string) => void;
  setAuth: (user: UserPublic, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,

      setUser: (user) => {
        syncLocale(user);
        syncMotion(user);
        set({ user });
      },

      setAccessToken: (accessToken) => set({ accessToken }),

      setAuth: (user, accessToken) => {
        syncLocale(user);
        syncMotion(user);
        set({ user, accessToken, isAuthenticated: true });
      },

      clearAuth: () =>
        set({ user: null, accessToken: null, isAuthenticated: false }),
    }),
    {
      name: 'freelancecrm-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.user) {
          syncLocale(state.user);
          syncMotion(state.user);
        }
      },
    },
  ),
);
